import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSwipeable } from "react-swipeable";
import { getAllMarketData, getmarginValues, placeOrder } from "../../services/allApiService";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import Grow from '@mui/material/Grow';
import { getClientData, toIndianRupeeFormat } from "../custom";
import { useSelector, useDispatch } from 'react-redux';
import {
    startMarketDataPolling,
    stopMarketDataPolling,
    fetchMarketDataRequest
} from '../../store/reducers/addorderReducer';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Grow ref={ref} timeout={3000} {...props} />
});


function EditOrder() {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const marketData = useSelector(state => state.addorder?.marketData || state.market?.marketData || []);

    const {
        limit_order_id,
        buttonType,
        scriptName,
        exchange,
        price,
        lotssize,
        netChange,
        percentChange,
        script_id,
        exchange_id,
        squareOff,
        qty,
        expiry,
        token,
        type,
        order_type,
        orderId,
    } = location.state || {};

    const [activeTab, setActiveTab] = useState('market');

    const [inputPrice, setInputPrice] = useState(null);

    const [qtyValue, setQtyValue] = useState(null);
    const [lotsValue, setLotsValue] = useState(1.00);
    const [orderPlacedDialog, setOrderPlacedDialog] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Financial state
    const [marginData, setMarginData] = useState(1);
    const [marginOvernightData, setMarginOvernightData] = useState(1);
    const [marginOvernightDataLive, setMarginOvernightDataLive] = useState(null);
    const [marginDataLive, setMarginDataLive] = useState(null);

    const parsedUserData = getClientData();

    const isUsingM = true;

    const handleClose = () => {
        setOrderPlacedDialog(false);
        navigate("/order");
    };

    // fatch margin value
    useEffect(() => {
        if (!script_id) return;

        const fetchMargin = async () => {
            try {

                const data = await getmarginValues(script_id);

                setMarginData(data?.data?.margin_balance);
                setMarginOvernightData(data?.data?.margin_overnight_balance);
            } catch (err) {
                console.error('Margin API error', err);
            }
        };
        fetchMargin();
    }, [script_id]);

    useEffect(() => {
        if (token && exchange) {
            const symbolTokensArray = [{
                token: token,
                script_id: script_id,
                exchange_id: exchange_id,
                id: null
            }];

            // Start polling for market data every 3 seconds
            dispatch(startMarketDataPolling(exchange, symbolTokensArray, 1000));

            // Fetch initial data
            dispatch(fetchMarketDataRequest(exchange, symbolTokensArray));
        }

        // Cleanup: Stop polling when component unmounts
        return () => {
            dispatch(stopMarketDataPolling());
        };
    }, [token, exchange, dispatch, script_id, exchange_id]);

    const getSymbolPrice = (symbolToken) => {
        const symbol = marketData.find(item =>
            String(item?.symbolToken) === String(symbolToken)
        );
        let price = type === 'buy' ? symbol?.depth.sell[0]?.price : symbol?.depth.buy[0]?.price;

        return symbol ? (price || symbol.ltp) : null;
    };

    const getSymbolData = (symbolToken) => {
        if (!symbolToken || !marketData || marketData.length === 0) {
            return null;
        }

        return marketData.find(item =>
            String(item?.symbolToken) === String(symbolToken)
        );
    };

    // Get live price from Redux store - FIXED
    const livePrice = getSymbolPrice(token);
    const symbolData = getSymbolData(token);

    useEffect(() => {
        setInputPrice(price !== undefined ? price : null);
        setQtyValue(qty !== undefined ? qty : null);
        setLotsValue(lotssize !== undefined ? lotssize : null);

    }, []);

    useEffect(() => {
        if (marginData && marginOvernightData && inputPrice && qtyValue) {
            updateFinancialCalculations(inputPrice, qtyValue);
        }
    }, [marginData, marginOvernightData, inputPrice, qtyValue]);

    useEffect(() => {
        if (order_type && ["limit", "market", "stoploss"].includes(order_type)) {
            setActiveTab(order_type);
        }
    }, [order_type])

    const handleSwipeComplete = async () => {
        if (isLoading) return;
        setIsLoading(true);
        try {
            const price = inputPrice;
            const qty = qtyValue;
            const lotSize = lotsValue;
            const circuit_low = marketData[0]?.lowerCircuit;
            const circuit_high = marketData[0]?.upperCircuit;
            const high = marketData[0]?.high;
            const low = marketData[0]?.low;

            let marketPrice = null;

            if (activeTab === "stoploss") {
                marketPrice = marketData[0]?.ltp;

            } else if (activeTab === "limit") {
                if (type === 'buy') {
                    marketPrice = marketData[0]?.depth?.buy[0]?.price;
                } else {
                    marketPrice = marketData[0]?.depth?.sell[0]?.price;

                }
            } else if (activeTab === "market") {
                marketPrice = livePrice;
            }
            const response = await placeOrder(script_id, price, lotSize, qty, exchange_id, type, activeTab, expiry, token, orderId, squareOff, circuit_low, circuit_high, high, low, limit_order_id, marketPrice);

            if (response && response.success) {
                setOrderPlacedDialog(true);
            } else {
                toast.error(response?.message || "Failed to place order.");
            }
        } catch (error) {
            toast.error("An error occurred while placing the order.");
        } finally {
            setIsLoading(false);
        }
    };

    const handlePriceChange = (e) => {
        const value = e.target.value;
        if (/^\d*\.?\d*$/.test(value)) {
            setInputPrice(value);
            updateFinancialCalculations(value, qtyValue);

        }
    };

    // Financial calculations
    const updateFinancialCalculations = (price, qty) => {
        if (!price || !qty || qty <= 0) {
            setMarginDataLive(0);
            setMarginOvernightDataLive(0);
            return;
        }
        // Intraday margin
        if (marginData) {
            setMarginDataLive((price * qty) / marginData);
        }

        // Overnight margin
        if (marginOvernightData) {
            setMarginOvernightDataLive((price * qty) / marginOvernightData);
        }
    };

    const calculateLotSize = (qtyValue) => {
        const currentLots = lotssize || 1;
        const currentQty = qty || 1;

        return qtyValue > 0 ? (qtyValue * currentLots) / currentQty : 0;
    };


    const handleQtyChange = (e) => {
        const newValue = e.target.value;

        // Allow empty string while typing
        if (newValue === '') {
            setQtyValue('');
            setLotsValue(0.00);
            updateFinancialCalculations(0);
            return;
        }

        // Block invalid inputs
        if (isNaN(newValue)) return;

        const qtyValue = Number(newValue);
        if (qtyValue <= 0) return;

        const newLots = calculateLotSize(qtyValue);

        setQtyValue(qtyValue);
        setLotsValue(Number(newLots.toFixed(2)));
        updateFinancialCalculations(inputPrice, qtyValue);
    };


    const handleQtyIncrement = () => {
        const currentQty = qtyValue || 0;
        const newQty = currentQty + 1;

        const qtyPerLot = qtyValue / (lotsValue || 1);
        const newLots = newQty / qtyPerLot;

        setQtyValue(newQty);
        setLotsValue(Number(newLots.toFixed(2)));
        updateFinancialCalculations(inputPrice, newQty);
    };


    const handleQtyDecrement = () => {
        const currentQty = qtyValue || 1;

        if (currentQty > 1) {
            const newQty = currentQty - 1;

            const qtyPerLot = qtyValue / (lotsValue || 1);
            const newLots = newQty / qtyPerLot;

            setQtyValue(newQty);
            setLotsValue(Number(newLots.toFixed(2)));
            updateFinancialCalculations(inputPrice, newQty);
        }
    };

    const handleLotsIncrement = () => {
        const currentLots = Number(lotsValue) || 0;
        const qtyPerLot = qtyValue / (currentLots || 1);

        const step = currentLots >= 1 ? 1 : 0.01;

        const newLots = Number((currentLots + step).toFixed(2));

        const dynamicQty = Math.round(newLots * qtyPerLot);

        setLotsValue(newLots);
        setQtyValue(dynamicQty);
        updateFinancialCalculations(dynamicQty);
    };

    const handleLotsDecrement = () => {
        const currentLots = lotsValue || 1;
        const qtyPerLot = qtyValue / currentLots;

        const step = currentLots > 1 ? 1 : 0.01;

        const newLots = Number((currentLots - step).toFixed(2));
        // const newLots = Math.max(currentLots - step, 0.01);

        const dynamicQty = Math.round(newLots * qtyPerLot);

        setLotsValue(newLots);
        setQtyValue(dynamicQty);
        updateFinancialCalculations(dynamicQty);
    };



    const handleGoBack = () => {
        navigate(-1);
    };

    const firstBuyPrice = marketData?.[0]?.depth?.buy?.[0]?.price;
    const firstSellPrice = marketData?.[0]?.depth?.sell?.[0]?.price;
    return (
        <div className="flex items-center justify-center flex-col bg-black min-h-screen">
            <div className="h-[100vh] flex-grow max-w-sm min-w-[370px] w-full bg-gray-100
                [&::-webkit-scrollbar]:w-1 
                [&::-webkit-scrollbar-track]:bg-transparent 
                [&::-webkit-scrollbar-thumb]:bg-transparent 
                scrollbar-thin scrollbar-track-transparent scrollbar-thumb-transparent">

                {["buy", "sell"].includes(type) && (
                    <>
                        {/* Dynamic color classes */}
                        {(() => {
                            const isBuy = type === "buy";
                            const mainColor = isBuy ? "#089981" : "#f23645";
                            const mainColorClass = isBuy ? "text-[#089981]" : "text-[#f23645]";
                            const tabBgClass = isBuy ? "bg-[#089981] bg-opacity-25" : "bg-[#f23645] bg-opacity-25";
                            const tabActiveClass = isBuy ? "bg-[#089981] text-white" : "bg-[#f23645] text-white";
                            const tabInactiveClass = "bg-transparent text-black";
                            const btnBgClass = isBuy ? "bg-[#0899811a]" : "bg-[#f236451a]";
                            const btnTextClass = isBuy ? "text-[#089981]" : "text-[#f23645]";
                            const summaryBgClass = isBuy ? "bg-[#089981] bg-opacity-25" : "bg-[#f23645] bg-opacity-25";
                            const summaryTextClass = isBuy ? "text-[#089981]" : "text-[#f23645]";
                            const intradayValue = marginDataLive;
                            const overnightValue = marginOvernightDataLive;
                            const buttonBgColor = isBuy ? "#089981" : "#ff6464";
                            const borderColor = isBuy ? "#007928" : "#790000";

                            return (
                                <>
                                    <div className="bg-gray-100 p-3">
                                        <div className="flex items-center justify-between w-full">
                                            {/* Left Side Content */}
                                            <div className="flex items-center space-x-2">
                                                <span className="font-black">
                                                    <ChevronLeft size={16} onClick={handleGoBack} />
                                                </span>
                                                <span className="font-lato font-semibold text-black text-[14px]">
                                                    {scriptName}
                                                </span>
                                                <span className="font-lato text-black bg-white text-[12px] font-medium uppercase py-1 px-2 rounded-sm">
                                                    {exchange}
                                                </span>
                                            </div>
                                            {/* Right Side Content */}
                                            <span className={`font-lato font-semibold text-[18px] ml-auto ${mainColorClass}`}>
                                                {livePrice}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <div className="flex items-center">
                                                <span className="text-gray-500 text-[12px] p-2 ml-4">
                                                    Lots size : {lotssize}
                                                </span>
                                            </div>
                                            <span className="text-gray-500 text-[12px]">
                                                {(marketData[0]?.netChange) >= 0
                                                    ? `+${marketData[0]?.netChange}`
                                                    : marketData[0]?.netChange}
                                                {` (`}
                                                {marketData[0]?.percentChange}
                                                {`%)`}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="bg-white p-3">
                                        <div className="flex items-center flex-row-reverse mt-1">
                                            <div className={`flex ${tabBgClass} rounded-[10px]`}>
                                                {["limit", "market", "stoploss"].map(tab => (
                                                    <a
                                                        key={tab}
                                                        role="tab"
                                                        onClick={() => {
                                                            setActiveTab(tab);
                                                        }}
                                                        className={`${activeTab === tab ? tabActiveClass : tabInactiveClass
                                                            } font-lato text-[14px] rounded-[10px] text-center w-[100px] h-[36px] py-[8px] px-[14px] cursor-pointer`}
                                                    >
                                                        {tab.toUpperCase()}
                                                    </a>
                                                ))}
                                            </div>
                                        </div>
                                        <label className="text-[14px] leading-[21px] text-[#1a1a1a] font-lato">
                                            Price
                                        </label>
                                        <div className="flex items-center justify-between mt-1">
                                            <div>
                                                <input
                                                    value={inputPrice}
                                                    type="text"
                                                    id="price"
                                                    readOnly={activeTab === "market"}
                                                    onChange={handlePriceChange}
                                                    className="w-[150px] h-[36px] bg-white border rounded-[5px] text-[1rem] px-[0.75rem] py-[0.375rem]"
                                                />
                                            </div>
                                        </div>
                                        {/* Qty & Lots Section */}
                                        <div className="flex justify-between gap-2 mt-8">
                                            {/* Qty Column */}
                                            <div className="flex flex-col items-left w-1/2">
                                                <label className="text-[14px] mb-[8px] leading-[21px] text-[#1a1a1a] font-lato">
                                                    Qty
                                                </label>
                                                <div className="flex items-center justify-between w-full mt-1 rounded-md py-1">
                                                    <button
                                                        className={`w-[36px] h-[36px] leading-[30px] ${btnBgClass} rounded-[4px] ${btnTextClass} text-[24px]`}
                                                        onClick={handleQtyDecrement}
                                                    >-</button>
                                                    <input
                                                        id={isBuy ? "buy_qty" : "sell_qty"}
                                                        type="number"
                                                        placeholder="empty"
                                                        value={qtyValue}
                                                        onChange={handleQtyChange}
                                                        className="w-[80px] h-[36px] border text-center bg-white font-semibold px-[6px] leading-[21px] text-[14px] text-[#6e6b7b] rounded-[4px]"
                                                    />
                                                    <button
                                                        className={`w-[36px] h-[36px] leading-[30px] ${btnBgClass} rounded-[4px] ${btnTextClass} text-[24px]`}
                                                        onClick={handleQtyIncrement}
                                                    >+</button>
                                                </div>
                                            </div>
                                            {/* Lots Column */}
                                            <div className="flex flex-col items-left w-1/2">
                                                <label className="text-[14px] mb-[8px] leading-[21px] text-[#1a1a1a] font-lato">
                                                    Lots
                                                </label>
                                                <div className="flex items-center justify-between w-full mt-1 rounded-md py-1">
                                                    <button
                                                        className={`w-[36px] h-[36px] leading-[30px] ${btnBgClass} rounded-[4px] ${btnTextClass} text-[24px]`}
                                                        onClick={handleLotsDecrement}
                                                    >-</button>
                                                    <input
                                                        id={isBuy ? "buy_lots" : "sell_lots"}
                                                        type="text"
                                                        value={(lotsValue ?? 0)}
                                                        readOnly
                                                        className="w-[80px] h-[36px] border text-center bg-white font-semibold px-[6px] leading-[21px] text-[14px] text-[#6e6b7b] rounded-[4px]"
                                                    />
                                                    <button
                                                        className={`w-[36px] h-[36px] leading-[30px] ${btnBgClass} rounded-[4px] ${btnTextClass} text-[24px]`}
                                                        onClick={handleLotsIncrement}
                                                    >+</button>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-center m-[2rem] p-[0rem] bg-gray-100">
                                            <label className="text-[1rem] text-center p-[1rem] leading-[21px] text-[#1a1a1a] font-lato">
                                                Available Balance : {toIndianRupeeFormat(parsedUserData?.client_account?.balance)}
                                            </label>
                                        </div>
                                    </div>
                                    <div className="space-y-10 p-4 fixed bottom-0 left-1/2 transform -translate-x-1/2 w-[100vh] max-w-[380px] pb-[20px] bg-white">
                                        <div className="flex items-center justify-between mt-1 space-x-4">
                                            <span className={`flex items-center font-lato justify-center w-[50%] h-[50px] text-center ${summaryBgClass} rounded-[3px] text-[12px]`}>
                                                INTRADAY : {(intradayValue ?? 0).toFixed(2)}
                                            </span>
                                            <span className={`flex items-center font-lato justify-center w-[50%] h-[50px] text-center ${summaryBgClass} rounded-[3px] text-[12px]`}>
                                                OVER NIGHT : {(overnightValue ?? 0).toFixed(2)}
                                            </span>
                                        </div>
                                        <div className="flex justify-center">
                                            <Button
                                                disabled={isLoading}
                                                className={`rounded-[8px] text-white py-4 px-[33px] text-[16px] font-semibold leading-[20px] flex flex-col items-center`}
                                                onClick={handleSwipeComplete}
                                                type={type}
                                                style={{
                                                    backgroundColor: isLoading ? "#9ca3af" : buttonBgColor,
                                                    borderColor: borderColor
                                                }}
                                            >
                                                {isLoading ? "Processing..." : "Update Order"}
                                            </Button>
                                        </div>
                                    </div>
                                </>
                            );
                        })()}
                    </>
                )}

                <Dialog
                    open={orderPlacedDialog}
                    onClose={() => { }}
                    TransitionComponent={Transition}
                    keepMounted
                    disableEscapeKeyDown
                    PaperProps={{
                        style: {
                            padding: '10px 0px',
                            borderRadius: '20px',
                            width: '360px',
                            boxShadow: '0px 10px 25px rgba(0, 0, 0, 0.2)',
                            background: 'linear-gradient(145deg, #ffffff, #f0f0f0)',
                        },
                    }}
                    BackdropProps={{
                        style: {
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            pointerEvents: 'none',
                        },
                    }}
                >
                    <DialogTitle
                        style={{
                            textAlign: 'center',
                            fontWeight: 'bold',
                            fontSize: '20px',
                            color: '#4caf50',
                            marginBottom: '10px',
                        }}
                    >
                        🎉 Order Placed Successfully!
                    </DialogTitle>

                    <DialogContent
                        style={{
                            textAlign: 'center',
                            fontSize: '16px',
                            color: '#555',
                            marginBottom: '20px',
                        }}
                    >
                        Your order has been placed and is being processed.
                    </DialogContent>

                    <DialogActions style={{ justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            onClick={handleClose}
                            style={{
                                backgroundColor: '#4caf50',
                                color: '#fff',
                                fontWeight: 'bold',
                                padding: '8px 20px',
                                borderRadius: '8px',
                                boxShadow: '0px 4px 12px rgba(76, 175, 80, 0.4)',
                            }}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

            </div>
            <ToastContainer />
        </div>
    );
}

export default EditOrder;